import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, FileText, LogOut, Home, Users, Heart, Building2, Brain, Pill, Shield, Stethoscope, AlertTriangle } from 'lucide-react';

// Service Navigation HIPAA Modal
const ServiceHIPAAModal = ({ isOpen, onAccept, onDecline, targetService }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  
  React.useEffect(() => {
    if (isOpen) setAcknowledged(false);
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-white" />
            <h3 className="text-lg font-bold text-white">HIPAA Authorization Required</h3>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">Accessing <strong>{targetService}</strong> may expose Protected Health Information (PHI).</p>
          </div>
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="w-4 h-4 rounded text-orange-500" />
            <span className="text-sm text-gray-700 font-medium">I agree to comply with HIPAA regulations</span>
          </label>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button onClick={onDecline} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm">Cancel</button>
          <button onClick={onAccept} disabled={!acknowledged} className={`px-4 py-2 rounded-lg text-sm font-medium ${acknowledged ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>Proceed</button>
        </div>
      </div>
    </div>
  );
};

const ClinicLayout = ({ children, activeTab = 'desktop' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hipaaModal, setHipaaModal] = useState({ open: false, target: '', href: '' });

  const handleServiceClick = (e, serviceName, href) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    setHipaaModal({ open: true, target: serviceName, href });
  };

  const serviceCategories = [
    { name: 'Home Health', icon: Home, color: 'bg-green-500', href: '/dashboard' },
    { name: 'Home Care', icon: Users, color: 'bg-purple-500', href: '/dashboard/patients' },
    { name: 'Hospice', icon: Heart, color: 'bg-blue-500', href: '/dashboard/clinical' },
    { name: 'Clinic', icon: Building2, color: 'bg-teal-500', href: '/dashboard/clinic' },
    { name: 'Behavioral Health', icon: Brain, color: 'bg-orange-500', href: '/dashboard/behavioral-health' },
    { name: 'DME', icon: Pill, color: 'bg-amber-500', href: '/dashboard/billing' },
    { name: 'Case Management', icon: Shield, color: 'bg-red-500', href: '/dashboard/patients' },
    { name: 'Concierge Visiting Doctors', icon: Stethoscope, color: 'bg-purple-600', href: '/concierge' },
  ];

  const mainTabs = [
    { id: 'desktop', label: 'Desktop', href: '/dashboard/clinic' },
    { id: 'appointments', label: 'Appointments', href: '/dashboard/clinic/appointments' },
    { id: 'patient-visits', label: 'Patient Visits', href: '/dashboard/clinic/patient-visits' },
    { id: 'claims-billing', label: 'Claims/Billing', href: '/dashboard/clinic/claims' },
    { id: 'accounting', label: 'Accounting', href: '/dashboard/clinic/accounting' },
    { id: 'manage-patients', label: 'Manage Patients', href: '/dashboard/clinic/patients' },
    { id: 'patient-portal', label: 'Patient Portal', href: '/dashboard/clinic/portal' },
    { id: 'manage-office', label: 'Manage Office', href: '/dashboard/clinic/office' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#e8e8e8]">
      {/* Service Category Buttons */}
      <div className="bg-gradient-to-r from-[#ea580c] to-[#f59e0b] py-2 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-4 md:grid-cols-8 gap-1.5">
            {serviceCategories.map((cat, idx) => (
              <button
                key={idx}
                onClick={(e) => handleServiceClick(e, cat.name, cat.href)}
                className={`${cat.color} text-white text-[11px] px-2 py-1.5 rounded font-medium shadow-sm flex items-center justify-center gap-1 hover:opacity-90`}
              >
                <cat.icon className="w-3 h-3" />
                {cat.name === 'Concierge Visiting Doctors' ? 'Concierge' : cat.name === 'Behavioral Health' ? 'Behavioral' : cat.name === 'Case Management' ? 'Case Mgmt' : cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* HIPAA Modal */}
      <ServiceHIPAAModal
        isOpen={hipaaModal.open}
        onAccept={() => { setHipaaModal({ open: false, target: '', href: '' }); navigate(hipaaModal.href); }}
        onDecline={() => setHipaaModal({ open: false, target: '', href: '' })}
        targetService={hipaaModal.target}
      />

      {/* Top Header - Light gray with links */}
      <div className="bg-white border-b">
        <div className="max-w-full mx-auto px-4">
          <div className="flex items-center justify-between h-12">
            {/* Left - Logo and Links */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#2563eb] rounded flex items-center justify-center">
                  <span className="text-white font-bold text-xs">IC</span>
                </div>
                <div>
                  <span className="text-[#2563eb] font-semibold text-lg">Ivory Clinic</span>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <Link to="/dashboard" className="text-[#2563eb] hover:underline">Home</Link>
                <span className="text-gray-400">|</span>
                <Link to="/dashboard/clinic/office" className="text-[#2563eb] hover:underline">Service Center</Link>
              </div>
            </div>

            {/* Right - User info and quick links */}
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-600">Welcome, IvoryHome. You are logged in.</span>
              <div className="flex items-center gap-3 text-[#2563eb]">
                <Link to="#" className="hover:underline">Intake/Agreement Forms</Link>
                <Link to="/dashboard/clinic/accounting" className="hover:underline">Accounting</Link>
                <Link to="/dashboard/clinic/claims" className="hover:underline">Repairable Claims</Link>
                <Link to="/dashboard/clinic/portal" className="hover:underline">Patient Portal</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Header with Logout */}
      <div className="bg-[#f0f0f0] border-b">
        <div className="max-w-full mx-auto px-4">
          <div className="flex items-center justify-between h-8 text-xs">
            <div className="flex items-center gap-3 text-[#2563eb]">
              <button onClick={handleLogout} className="hover:underline">&lt;&lt;LOGOUT</button>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">HIPAA</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">About Us / Privacy</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">Professional & Institutional Payor list</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">Forms</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">Tech Training</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">Remote Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Tabs - Dark Blue */}
      <div className="flex justify-center pt-4">
        <div className="flex items-center">
          {mainTabs.map((tab, index) => {
            const isActive = location.pathname === tab.href || location.pathname.startsWith(tab.href + '/');
            return (
              <Link
                key={tab.id}
                to={tab.href}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-white text-[#1e3a5f] border-t border-l border-r border-gray-300 rounded-t'
                    : 'bg-[#1e3a5f] text-white hover:bg-[#2c5282]'
                } ${index === 0 ? 'rounded-tl' : ''} ${index === mainTabs.length - 1 ? 'rounded-tr' : ''}`}
                data-testid={`clinic-tab-${tab.id}`}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Page Content */}
      <div className="flex justify-center">
        <div className="w-full max-w-6xl bg-white border border-gray-300 shadow-sm min-h-[500px] relative">
          {children}
          
          {/* Right Sidebar - Help Center & Request Samples */}
          <div className="absolute right-0 top-0 flex flex-col gap-2 p-2">
            <button className="w-16 h-16 bg-gray-100 border border-gray-300 rounded flex flex-col items-center justify-center text-xs text-gray-600 hover:bg-gray-200">
              <HelpCircle className="w-6 h-6 mb-1" />
              Help Center
            </button>
            <button className="w-16 h-16 bg-gray-100 border border-gray-300 rounded flex flex-col items-center justify-center text-xs text-gray-600 hover:bg-gray-200">
              <FileText className="w-6 h-6 mb-1" />
              Request Samples
            </button>
          </div>
        </div>
      </div>

      {/* Shortcut Keys Footer */}
      <div className="flex justify-center mt-2">
        <div className="text-xs text-gray-600">
          <span className="font-medium">Shortcut Keys:</span>
          <span className="text-[#2563eb] ml-2">Alt F1-Help</span>
          <span className="mx-1">|</span>
          <span className="text-[#2563eb]">F2-Add New Appointment</span>
          <span className="mx-1">|</span>
          <span className="text-[#2563eb]">F7-Add New Visit</span>
          <span className="mx-1">|</span>
          <span className="text-[#2563eb]">F8-Add New Payment</span>
          <span className="mx-1">|</span>
          <span className="text-[#2563eb]">F9-Add New Patient</span>
          <span className="mx-1">|</span>
          <span className="text-[#2563eb]">F10-Patient List</span>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8 text-xs text-gray-500">
        <div className="inline-block bg-gray-200 rounded-t-lg px-8 py-3">
          <p>© 2026 IvoryClinic.com</p>
          <p>WEBA03</p>
          <p>5.91126</p>
        </div>
      </div>
    </div>
  );
};

export default ClinicLayout;
